import React, { useContext, useEffect, useState } from 'react';
import { navigate } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import Heading from '@vfuk/core-heading';
import {
  bgWhite,
  bgAbbey,
  imageBlock,
  articleSummary,
} from './styles/Article.styles';
import RichTextElement from '../pageBuilder/Elements/RichText';
import Icon from '@vfuk/core-icon';
import CardProps from './types/Card.types';
import {
  cardContainer,
  iconsAndTextContainer,
  iconTextStyle,
  redSmallDateStyling,
  textBlock,
  imageIconStyle,
  redTextLinkWrapper,
} from './styles/Card.styles';
import fireButtonAndLinkClicksEvent from '../../helpers/fireButtonAndLinkClicksEvent';
import { DirectionContext } from '../../contexts/DirectionContext';

const Card = ({ data, backgroundColor = 'white' }: CardProps) => {
  const {
    id,
    name,
    cardSummary,
    mainImage,
    imageIcon,
    redLink,
    iconsAndText = [],
  } = data;

  const isMobile = checkWindowSize();

  const direction = useContext(DirectionContext);

  function checkWindowSize() {
    const [isMobile, setIsMobile] = useState(false);
    if (typeof window !== 'undefined') {
      useEffect(() => {
        function handleResize() {
          if (window.innerWidth < 641) {
            setIsMobile(true);
          }
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
      }, []);
    }
    return isMobile;
  }

  const backgroundColorCSS = backgroundColor === 'white' ? bgWhite : bgAbbey;

  const cardCss = [backgroundColorCSS[0]?.concat(cardContainer[0])];

  const iconTextRow = iconsAndText?.map(icon => {
    return (
      <div key={icon.id} css={iconTextStyle} style={{ color: icon.color }}>
        <Icon
          name={icon.iconRef}
          size={icon.iconSize}
          appearance={icon.appearance || 'primary'}
          inverse={icon.inverse}
        />
        <span>{icon.label}</span>
      </div>
    );
  });

  const handleDesktopClick = () => {
    if (redLink) {
      fireButtonAndLinkClicksEvent({
        event_label: redLink.name,
        event_action: 'link click',
      });
      navigate(redLink.url);
    }
  };

  const handleMobileClick = () => {
    if (redLink) {
      if (typeof window !== 'undefined') {
        if (window.innerWidth < 641) {
          fireButtonAndLinkClicksEvent({
            event_label: redLink.name,
            event_action: 'link click',
          });
          navigate(redLink.url);
        }
      }
    }
  };

  const redTextLink = redLink ? (
    <div css={redTextLinkWrapper}>
      <div onClick={handleDesktopClick} className="red-text-link">
        <div className="red-text-link-label">{redLink.name}</div>
        <div
          className="red-text-link-icon"
          style={
            direction === 'rtl'
              ? { transform: 'scaleX(-1)' }
              : { transform: 'unset' }
          }
        >
          <Icon name="chevron-right" size={3} appearance="secondary" />
        </div>
      </div>
    </div>
  ) : null;

  const cardImageIcon = imageIcon ? (
    <div
      css={imageIconStyle}
      style={direction === 'rtl' ? { right: '20px' } : { left: '20px' }}
    >
      <Icon name={imageIcon.iconRef} size={5} appearance="primary" inverse />
    </div>
  ) : null;

  let redSmallDate;

  if (cardSummary && cardSummary.raw) {
    const parsedRawSummary = JSON.parse(cardSummary.raw);
    for (const content of parsedRawSummary.content) {
      for (const subContent of content.content) {
        if (
          subContent.value &&
          typeof subContent.value === 'string' &&
          subContent.value.includes('redSmallDate:')
        ) {
          redSmallDate = subContent.value;
          const redSmallDateIndex = content.content.findIndex(
            (subContent: any) => {
              return subContent.value.includes('redSmallDate:');
            }
          );
          redSmallDate = redSmallDate.replace('redSmallDate:', '');
          content.content.splice(redSmallDateIndex);
          cardSummary.raw = JSON.stringify(parsedRawSummary);
        }
      }
    }
  }

  return (
    <div
      id={id}
      css={articleSummary}
      onClick={redLink ? handleMobileClick : undefined}
      style={
        isMobile ? { cursor: 'pointer', margin: '10px' } : { margin: '10px' }
      }
    >
      <div
        css={cardCss}
        style={{ position: 'relative', borderRadius: '7px' }}
        className="card"
        data-testid={`card-${name}`}
      >
        {mainImage && (
          <GatsbyImage
            image={getImage(mainImage) as IGatsbyImageData}
            css={imageBlock}
            alt={
              mainImage.description || mainImage.title
            }
            style={{ borderTopLeftRadius: '7px', borderTopRightRadius: '7px' }}
          />
        )}
        {cardImageIcon && cardImageIcon}
        {cardImageIcon && <br />}
        <div
          css={textBlock}
          style={{ textAlign: direction === 'rtl' ? 'start' : 'inherit' }}
        >
          {redSmallDate && <div css={redSmallDateStyling}>{redSmallDate}</div>}
          <Heading text={name} weight={3} size={2} noMargin={true} />
          <RichTextElement
            rawRichText={cardSummary?.raw}
            className="card-text"
          />
        </div>
        {redTextLink && redTextLink}
        <div css={iconsAndTextContainer}>
          {iconsAndText?.length > 0 && iconTextRow}
        </div>
      </div>
    </div>
  );
};

export default Card;
